import { Alert } from '@creditornot/cb-alert';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import { useAuthActions, useAuthState } from '@creditornot/wolt-auth/react';
import React from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import { Redirect, Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

import CheckYourEmail from './CheckYourEmail/CheckYourEmail';
import Contracts from './Contracts/Contracts';
import Thanks from './Contracts/Thanks';
import MerchantSignUpForm from './MerchantSignUpForm/MerchantSignUpForm';
import Start from './MerchantSignUpForm/Start';
import { merchantSignUpFormValidationSchema } from './MerchantSignUpForm/ValidationSchema';
import useSigneeInformation from './MerchantSignUpForm/useSigneeInformation';
import { createUser, requestSignUpEmailForDraftMerchant } from '../../apis/merchant-onboarding-service/api';

const Root = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${cbSemanticCssVars.surfaceMain};
`;

const SpinnerContainer = styled.div`
  height: 100%;
  display: flex;
  flex: 1 1 auto;
  justify-content: center;
  align-items: center;
`;

const ErrorAlert = styled(Alert).attrs({
  variant: 'error',
})`
  margin-block: 16px;
`;

type MerchantSignupHistoryState = {
  phoneNumber?: string;
  onboardingToken?: string;
  emailToken?: string;
  success?: boolean;
};

const MerchantSignUp = () => {
  const { url } = useRouteMatch();
  const history = useHistory<MerchantSignupHistoryState>();
  const merchantSignupHistoryState = history.location.state || {};
  const { user } = useAuthState();
  const { login, logout } = useAuthActions();

  const {
    data: userInformation,
    isFetching: isFetchingUserInformation,
    isError: loadingUserInformationFailed,
  } = useSigneeInformation(merchantSignupHistoryState?.onboardingToken);

  if (isFetchingUserInformation) {
    return (
      <SpinnerContainer data-testid="merchant-sign-up-spinner">
        <Spinner size="medium" />
      </SpinnerContainer>
    );
  }

  if (loadingUserInformationFailed) {
    return (
      <ErrorAlert data-testid="MerchantSignUpFormContactInfoError">
        <FormattedMessage id="merchant-sign-up.message.contact-info" />
      </ErrorAlert>
    );
  }

  return (
    <Root>
      <Switch>
        <Route
          exact
          path={url}
          component={() => {
            return (
              <Start
                onStart={async (onboardingToken: string, emailToken: string) => {
                  if (user) {
                    await logout();
                  }
                  history.push(`${url}/form`, { onboardingToken, emailToken });
                }}
              />
            );
          }}
        />
        <Route
          exact
          path={`${url}/form`}
          component={() => {
            return !merchantSignupHistoryState?.onboardingToken || !merchantSignupHistoryState?.emailToken ? (
              <Redirect to={url} />
            ) : (
              <MerchantSignUpForm
                userInformation={userInformation}
                onboardingToken={merchantSignupHistoryState.onboardingToken}
                emailToken={merchantSignupHistoryState.emailToken}
                onSubmit={createUser}
                onSuccess={async (emailToken: string) => {
                  try {
                    await login({
                      grantType: 'email_login',
                      audience: 'merchant-admin',
                      token: emailToken,
                    });
                    history.replace('/');
                  } catch (error) {
                    toast.error(error.message);
                  }
                }}
                onInvalidEmailToken={async (onboardingToken: string) => {
                  try {
                    await requestSignUpEmailForDraftMerchant({ onboardingToken });
                    history.replace(`${url}/check-your-email`, { success: false });
                  } catch (error) {
                    toast.error(error.message);
                  }
                }}
                onUserAlreadySignedUp={() => {
                  history.replace('/');
                }}
                validationSchema={merchantSignUpFormValidationSchema}
              />
            );
          }}
        />
        <Route exact path={`${url}/success`} component={Contracts} />
        <Route exact path={`${url}/thanks`} component={Thanks} />
        <Route exact path={`${url}/check-your-email`}>
          <CheckYourEmail success={!!merchantSignupHistoryState.success} />
        </Route>
      </Switch>
    </Root>
  );
};

export default MerchantSignUp;
